// document.json schema + migrations (DRAW_PLAN §4), in the style of the Photo Booth sidecar: validate, upgrade
// older schemas step by step, refuse newer ones (never overwrite what a newer build wrote).
import type { Doc } from './types';

export const DOC_SCHEMA = 1;
export const EPD_APP = 'eat-photo-design';

export class DocError extends Error {}

/** The file's top level: the document plus bookkeeping. */
export type DocFile = { schema: number; app: typeof EPD_APP; appVersion: string; savedAt: string; doc: Doc };

/** schema n → n+1 upgrades (none yet: schema 1 is the first). */
const MIGRATIONS: Record<number, (f: Record<string, unknown>) => Record<string, unknown>> = {};

export function parseDocFile(text: string): DocFile {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new DocError('document.json is not valid JSON');
  }
  if (!raw || typeof raw !== 'object') throw new DocError('document.json is not an object');
  let f = raw as Record<string, unknown>;
  if (f.app !== EPD_APP) throw new DocError('not an EAT Photo Design document');
  let schema = typeof f.schema === 'number' ? f.schema : NaN;
  if (!Number.isInteger(schema) || schema < 1) throw new DocError(`invalid schema ${String(f.schema)}`);
  if (schema > DOC_SCHEMA)
    throw new DocError(`document schema ${schema} is newer than this app (${DOC_SCHEMA})`);
  while (schema < DOC_SCHEMA) {
    const step = MIGRATIONS[schema];
    if (!step) throw new DocError(`no migration from schema ${schema}`);
    f = step(f);
    schema++;
  }
  const doc = f.doc as Doc | undefined;
  if (!doc || !Array.isArray(doc.artboards) || !Array.isArray(doc.layers))
    throw new DocError('document is missing artboards or layers');
  return {
    schema: DOC_SCHEMA,
    app: EPD_APP,
    appVersion: typeof f.appVersion === 'string' ? f.appVersion : 'unknown',
    savedAt: typeof f.savedAt === 'string' ? f.savedAt : new Date(0).toISOString(),
    doc,
  };
}

export function serializeDoc(doc: Doc, appVersion = '0.1.0', now = new Date()): string {
  const f: DocFile = { schema: DOC_SCHEMA, app: EPD_APP, appVersion, savedAt: now.toISOString(), doc };
  return JSON.stringify(f, null, 1); // pretty-printed for diffability
}
